import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, FileWarning, Sparkles, Phone, Calendar as CalIcon, TrendingUp, Clock } from "lucide-react";
import { api, API } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { StatusBadge } from "./Layout";

export default function CommandCenter() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/command-center").then(({ data }) => setData(data)).catch(() => {}); }, []);

  if (!data) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4" data-testid="command-center">
      <div className="flex items-center gap-2">
        <div className="w-1 h-6 bg-primary rounded-full" />
        <h2 className="font-heading text-lg font-semibold tracking-tight">Centro Operazioni</h2>
        <span className="text-xs text-muted-foreground">Dove concentrare il lavoro oggi</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Panel title="Pratiche urgenti" icon={AlertCircle} color="rose" count={data.urgent_tasks.length} testid="cc-urgent">
          {data.urgent_tasks.length === 0 ? <Empty text="Nessuna pratica urgente." /> :
            data.urgent_tasks.slice(0, 5).map((t) => (
              <ItemRow key={t.id} title={t.title} meta={t.due_date ? new Date(t.due_date).toLocaleDateString("it-IT") : t.priority} link={t.property_id ? `/properties/${t.property_id}` : "/tasks"} pill={t.priority === "high" ? "Alta" : "!"} pillCls="bg-rose-500 text-white" />
            ))
          }
        </Panel>

        <Panel title="Documenti mancanti" icon={FileWarning} color="amber" count={data.missing_docs.length} testid="cc-missing-docs">
          {data.missing_docs.length === 0 ? <Empty text="Documentazione completa ovunque." /> :
            data.missing_docs.slice(0, 5).map((p) => (
              <ItemRow key={p.id} title={p.title} meta={`${p.city || "—"} · ${p.doc_completeness || 0}%`} link={`/properties/${p.id}`} pill={`${p.doc_completeness || 0}%`} pillCls="bg-amber-100 text-amber-700" />
            ))
          }
        </Panel>

        <Panel title="Opportunità ≥ 85" icon={Sparkles} color="emerald" count={data.top_opportunities.length} testid="cc-top-opps">
          {data.top_opportunities.length === 0 ? <Empty text="Nessuna opportunità di alto livello." /> :
            data.top_opportunities.slice(0, 5).map((r) => (
              <ItemRow key={r.property.id} title={r.property.title} meta={`€ ${fmt(r.property.price)}`} link={`/properties/${r.property.id}`} pill={r.score.toString()} pillCls="bg-emerald-100 text-emerald-700 tabular-nums" />
            ))
          }
        </Panel>

        <Panel title="Proprietari da richiamare" icon={Phone} color="blue" count={data.owners_to_call.length} testid="cc-owners">
          {data.owners_to_call.length === 0 ? <Empty text="Tutti i proprietari sono aggiornati." /> :
            data.owners_to_call.slice(0, 5).map((o) => (
              <ItemRow key={o.id} title={o.name} meta={o.phone || o.email || "—"} link="/owners" pill="14g+" pillCls="bg-blue-100 text-blue-700" />
            ))
          }
        </Panel>

        <Panel title="Appuntamenti oggi" icon={CalIcon} color="violet" count={data.today_appointments.length} testid="cc-appts">
          {data.today_appointments.length === 0 ? <Empty text="Agenda libera oggi." /> :
            data.today_appointments.slice(0, 5).map((a) => (
              <ItemRow key={a.id} title={a.title} meta={new Date(a.starts_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} link="/calendar" pill={a.location ? "📍" : "📅"} pillCls="bg-violet-100 text-violet-700" />
            ))
          }
        </Panel>

        <Panel title="Top ROI" icon={TrendingUp} color="emerald" count={data.high_roi.length} testid="cc-roi">
          {data.high_roi.length === 0 ? <Empty text="Nessun ROI positivo ancora." /> :
            data.high_roi.slice(0, 5).map((r) => (
              <ItemRow key={r.property.id} title={r.property.title} meta={`€ ${fmt(r.property.price)}`} link={`/properties/${r.property.id}`} pill={`${r.roi}%`} pillCls="bg-emerald-100 text-emerald-700 tabular-nums" />
            ))
          }
        </Panel>

        <Panel title="Scadenze prossimi 7g" icon={Clock} color="slate" count={data.upcoming_deadlines.length} testid="cc-upcoming">
          {data.upcoming_deadlines.length === 0 ? <Empty text="Nessuna scadenza in vista." /> :
            data.upcoming_deadlines.slice(0, 5).map((t) => (
              <ItemRow key={t.id} title={t.title} meta={t.due_date ? new Date(t.due_date).toLocaleDateString("it-IT") : ""} link={t.property_id ? `/properties/${t.property_id}` : "/tasks"} pill={t.priority === "high" ? "Alta" : t.priority} pillCls="bg-slate-200 text-slate-700 capitalize" />
            ))
          }
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, color, count, children, testid }) {
  const cls = { rose: "border-l-rose-500", amber: "border-l-amber-500", emerald: "border-l-emerald-500", blue: "border-l-blue-500", violet: "border-l-violet-500", slate: "border-l-slate-400" }[color] || "border-l-primary";
  const iconCls = { rose: "text-rose-500", amber: "text-amber-500", emerald: "text-emerald-500", blue: "text-blue-500", violet: "text-violet-500", slate: "text-slate-500" }[color] || "text-primary";
  return (
    <Card className={`border-border/60 border-l-4 ${cls} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${iconCls}`} />
        <h4 className="font-heading font-medium text-sm">{title}</h4>
        <Badge variant="secondary" className="ml-auto tabular-nums text-[10px]">{count}</Badge>
      </div>
      <div className="space-y-1.5">{children}</div>
    </Card>
  );
}
function ItemRow({ title, meta, link, pill, pillCls }) {
  return (
    <Link to={link} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/40 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{title}</div>
        <div className="text-[10px] text-muted-foreground truncate">{meta}</div>
      </div>
      {pill && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${pillCls}`}>{pill}</span>}
    </Link>
  );
}
function Empty({ text }) { return <p className="text-xs text-muted-foreground text-center py-3">{text}</p>; }
function fmt(n) { return Intl.NumberFormat("it-IT").format(Math.round(n || 0)); }
